import { Path } from "@kform/core";
import * as React from "react";

import { InvalidPathError } from "../utils/errors";
import {
  formatNumericAsString,
  Numeric,
  parseStringAsNumeric,
} from "../utils/numericUtils";
import { DistributedOmit } from "../utils/typeUtils";
import { useLatestValues } from "../utils/useLatestValues";
import { Controller } from "./useController";
import { FormatterControllerState } from "./useFormatter";
import { InputOptions, InputOwnController, useInput } from "./useInput";

/**
 * Options available to the {@link useNumericInput} hook.
 */
export type NumericInputOptions<
  T extends Numeric | null = Numeric | null,
  TFormatted = string,
  TInput = HTMLInputElement,
> = DistributedOmit<InputOptions<T, TFormatted, TInput>, "format" | "parse"> &
  NumericInputOwnOptions<T, TFormatted, TInput>;

/**
 * Own options available to the {@link useNumericInput} hook.
 */
export interface NumericInputOwnOptions<
  T extends Numeric | null = Numeric | null,
  TFormatted = string,
  TInput = HTMLInputElement,
> {
  formatFromString?: (
    stringValue: string,
    state: FormatterControllerState<T, TFormatted>,
    input: TInput | null,
  ) => TFormatted;
  parseToString?: (
    formattedValue: TFormatted,
    state: FormatterControllerState<T, TFormatted>,
    input: TInput | null,
  ) => string;
}

/**
 * Controller used to read and control the form value associated with the
 * form input, exposing properties that should be set on the input itself.
 */
export type NumericInputController<
  T extends Numeric | null = Numeric | null,
  TFormatted = string,
  TInput = HTMLInputElement,
> = Controller<T> & NumericInputOwnController<TFormatted, TInput>;

/**
 * Own numeric input controller.
 */
export type NumericInputOwnController<
  TFormatted = string,
  TInput = HTMLInputElement,
> = InputOwnController<TFormatted, TInput>;

/**
 * Allowed Kotlin numeric types.
 */
const ALLOWED_NUMERIC_TYPES = [
  "Byte",
  "Char",
  "Short",
  "Int",
  "Long",
  "Float",
  "Double",
  "BigInteger",
  "BigDecimal",
  // Not exactly a numeric type, but it is sometimes useful to treat strings
  // with numeric patterns as "numeric"
  "String",
];

/**
 * Hook controlling the integration between a numeric value of the form manager
 * and an input of the form.
 * @param path Path of the numeric form value to access, relative to the current
 * path.
 * @param options Available options.
 * @throws {InvalidPathError} When the schema at {@link path} is not numeric.
 * @returns A controller used to read and control the numeric form value
 * associated with the form input, exposing properties that should be set on the
 * input itself.
 */
export function useNumericInput<
  T extends Numeric | null = Numeric | null,
  TFormatted = string,
  TInput = HTMLInputElement,
>(
  path?: Path | string,
  options?: undefined,
): NumericInputController<T, TFormatted, TInput>;
export function useNumericInput<
  T extends Numeric | null = Numeric | null,
  TFormatted = string,
  TInput = HTMLInputElement,
>(
  path: Path | string | undefined,
  options: NumericInputOptions<T, TFormatted, TInput>,
): NumericInputController<T, TFormatted, TInput>;
export function useNumericInput<
  T extends Numeric | null = Numeric | null,
  TFormatted = string,
  TInput = HTMLInputElement,
>(
  path: Path | string = Path.CURRENT,
  {
    formatFromString,
    parseToString,
    ...options
  }: NumericInputOptions<T, TFormatted, TInput> = {},
): NumericInputController<T, TFormatted, TInput> {
  const latestOptions = useLatestValues({ parseToString });

  // Input options
  const format = React.useCallback(
    (
      value: T | undefined,
      state: FormatterControllerState<T, TFormatted>,
      input: TInput | null,
    ): TFormatted => {
      const numberString = formatNumericAsString(value, state.schema);
      return formatFromString
        ? formatFromString(numberString, state, input)
        : (numberString as TFormatted);
    },
    [formatFromString],
  );
  const parse = React.useCallback(
    (
      formattedValue: TFormatted,
      state: FormatterControllerState<T, TFormatted>,
      input: TInput | null,
    ): T => {
      return parseStringAsNumeric(
        latestOptions.parseToString
          ? latestOptions.parseToString(formattedValue, state, input)
          : String(formattedValue ?? ""),
        state.schema,
      );
    },
    [latestOptions],
  );

  const inputController = useInput<T, TFormatted, TInput>(path, {
    format,
    parse,
    ...options,
  });

  // Validate schema type
  const { typeInfo } = inputController.useSchema();
  if (!ALLOWED_NUMERIC_TYPES.includes(typeInfo.name)) {
    throw new InvalidPathError(
      inputController.getState().path,
      "Unsupported schema: Expected schema representing (possibly nullable) " +
        "numeric values, but got schema representing values of type " +
        `'${typeInfo.toString()}'.`,
    );
  }

  return inputController;
}
